import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { ChatUpcomingTrip, ChatUpcomingTripLeg } from '@sabstravtech/obtservices/base';
import { EnterpriseMyBookingsService } from '@sabstravtech/obtservices/angular';

/** Shape of a rail leg's resolved basket detail (subset used by the chat card). */
interface RailWardDetail {
  departureStationName?: string;
  arrivalStationName?: string;
  departureDateTime?: string;
  arrivalDateTime?: string;
  supplierTickets?: Array<{ legs?: Array<{ operator?: string }> }>;
}
interface RailLegDetail {
  outwardDetail?: RailWardDetail;
  inwardDetail?: RailWardDetail;
}

/** Shape of a flight / Eurostar segment (both store the same flight structure). */
interface FlightSegment {
  originAirport?: string;
  originAirportName?: string;
  destinationAirport?: string;
  destinationAirportName?: string;
  dateTimeDeparting?: string;
  dateTimeArriving?: string;
  marketingCarrierName?: string;
}
interface FlightLegDetail {
  outboundFlights?: FlightSegment[];
  inboundFlights?: FlightSegment[];
}

/** Shape of a hotel leg's resolved basket detail (subset used by the chat card). */
interface HotelLegDetail {
  hotelName?: string;
  hotelAddress?: { [key: string]: string };
  numberOfRooms?: number;
  roomType?: string;
  rooms?: Array<{ checkIn?: string; checkOut?: string }>;
}

/** Shape of a car-hire leg's resolved basket detail. */
interface CarLocation {
  name?: string;
  address?: string;
  iata?: string;
}
interface CarLegDetail {
  vendorName?: string;
  car?: { class?: string; type?: string };
  pickUpLocation?: CarLocation;
  dropOffLocation?: CarLocation;
  pickUpDatetime?: string;
  dropOffDatetime?: string;
}

/** Shape of a lounge leg's resolved basket detail. */
interface LoungeLegDetail {
  name?: string;
  airportIata?: string;
  terminal?: string;
  destination?: string;
  arrivalDatetime?: string;
}

/** Shape of a parking leg's resolved basket detail. */
interface ParkingLegDetail {
  name?: string;
  airportIata?: string;
  arrivalDatetime?: string;
  departDatetime?: string;
}

/** Shape of a fast-track leg's resolved basket detail. */
interface FastTrackLegDetail {
  name?: string;
  airportIata?: string;
  terminal?: string;
  arrivalDatetime?: string;
}

/** Shape of a taxi / cab leg's resolved basket detail. */
interface TaxiLocation {
  buildingName?: string;
  streetAndNumber?: string;
  town?: string;
}
interface TaxiLegDetail {
  supplierName?: string;
  vehicleType?: string;
  fromLocation?: TaxiLocation;
  toLocation?: TaxiLocation;
  departs?: string;
}

/** A point-to-point timed leg, shared by rail, flight, Eurostar and taxi. */
interface JourneyRow {
  label: string;
  date: string;
  operator: string;
  departTime: string;
  departPoint: string;
  arriveTime: string;
  arrivePoint: string;
}

/** Title + location + label/value rows, shared by car, lounge and parking. */
interface ServiceRow {
  label: string;
  value: string;
}
interface ServiceDetailView {
  title: string;
  subtitle: string;
  rows: ServiceRow[];
}

/** One ticket reduced to what the card can actually show. */
interface ETicketRow {
  number: string;
  pdfLink: string;
  walletIos: string;
  walletAndroid: string;
  hasLinks: boolean;
}

/** Ticket delivery for one leg — the method decides which of the other fields is read. */
interface DeliveryView {
  method: 'ETICKET' | 'TOD' | 'PRINT';
  eTickets: ETicketRow[];
  todReferences: string[];
  note: string;
}

/** Pre-computed view of a single basket item, so the template binds plain values. */
interface TripItemView {
  kind: 'journey' | 'hotel' | 'service' | 'generic';
  label: string;
  status: string;
  isBooked: boolean;
  pnrLocator: string | null;
  supplierReference: string | null;
  journeyRows: JourneyRow[];
  hotelName: string;
  hotelLocation: string;
  checkIn: string;
  checkOut: string;
  nights: number | null;
  serviceDetail: ServiceDetailView | null;
  genericDates: string;
  delivery: DeliveryView | null;
}

/** Pre-computed view of the whole trip, built once when the input is set. */
interface TripView {
  reference: string;
  chips: string[];
  travellerName: string;
  travelDate: string;
  items: TripItemView[];
}

@Component({
  selector: 'app-chat-trip-card',
  standalone: false,
  templateUrl: './chat-trip-card.component.html',
  styleUrls: ['./chat-trip-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ChatTripCardComponent {
  view!: TripView;

  @Input() set trip(value: ChatUpcomingTrip) {
    this.view = this.buildView(value);
  }

  constructor(private myBookingsService: EnterpriseMyBookingsService) {}

  private buildView(trip: ChatUpcomingTrip): TripView {
    return {
      reference: trip.reference,
      chips: (trip.serviceTypes ?? []).map((type) => this.serviceTypeLabel(type)),
      travellerName:
        trip.items.map((i) => i.leadPassenger).find((name): name is string => !!name) ?? '',
      travelDate: this.formatLongDate(trip.earliestTravelDate),
      items: trip.items.map((item) => this.buildItemView(item))
    };
  }

  private buildItemView(item: ChatUpcomingTripLeg): TripItemView {
    const type = item.serviceType?.toUpperCase() ?? '';
    const kind = this.itemKind(type);
    const hotelStay = kind === 'hotel' ? this.hotelStay(item) : null;
    return {
      kind,
      label: this.serviceTypeLabel(item.serviceType),
      status: item.status,
      isBooked: item.status?.toUpperCase() === 'BOOKED',
      pnrLocator: item.pnrLocator,
      supplierReference: item.supplierReference,
      journeyRows: kind === 'journey' ? this.journeyRows(type, item) : [],
      hotelName: kind === 'hotel' ? this.hotelName(item) : '',
      hotelLocation: kind === 'hotel' ? this.hotelLocation(item) : '',
      checkIn: hotelStay?.checkIn ?? '',
      checkOut: hotelStay?.checkOut ?? '',
      nights: hotelStay?.nights ?? null,
      serviceDetail: kind === 'service' ? this.serviceDetail(type, item) : null,
      genericDates: kind === 'generic' ? this.genericDates(item) : '',
      delivery: this.deliveryView(item)
    };
  }

  /** Ticket delivery for one leg — null when the supplier gave us nothing presentable. */
  private deliveryView(item: ChatUpcomingTripLeg): DeliveryView | null {
    const method = item.deliveryMethod;
    if (!method) {
      return null;
    }
    const eTickets = (item.eTickets ?? []).map((ticket) => {
      const pdfLink = ticket.pdfLink ?? '';
      const walletIos = ticket.walletIos ?? '';
      const walletAndroid = ticket.walletAndroid ?? '';
      return {
        number: ticket.number ?? '',
        pdfLink,
        walletIos,
        walletAndroid,
        hasLinks: !!(pdfLink || walletIos || walletAndroid)
      };
    });
    const todReferences = (item.todReference ?? []).filter((ref): ref is string => !!ref);
    const note = item.deliveryNote ?? '';
    // A method with nothing behind it (ETICKET with neither links nor numbers, TOD with no
    // reference) renders as a heading over empty space — show nothing at all instead.
    const hasContent =
      eTickets.some((row) => row.hasLinks || row.number) || todReferences.length > 0 || !!note;
    return hasContent ? { method, eTickets, todReferences, note } : null;
  }

  private itemKind(type: string): TripItemView['kind'] {
    switch (type) {
      case 'RAIL':
      case 'FLIGHT':
      case 'EUROSTAR':
      case 'CAB':
      case 'TAXI':
        return 'journey';
      case 'HOTEL':
        return 'hotel';
      case 'CAR':
      case 'LOUNGE':
      case 'PARKING':
      case 'FAST_TRACK':
        return 'service';
      default:
        return 'generic';
    }
  }

  private journeyRows(type: string, item: ChatUpcomingTripLeg): JourneyRow[] {
    if (type === 'RAIL') {
      return this.railRows(item);
    }
    if (type === 'FLIGHT' || type === 'EUROSTAR') {
      return this.flightRows(item);
    }
    if (type === 'CAB' || type === 'TAXI') {
      return this.taxiRows(item);
    }
    return this.genericRow(item);
  }

  private serviceDetail(type: string, item: ChatUpcomingTripLeg): ServiceDetailView | null {
    if (type === 'CAR') {
      return this.carDetail(item);
    }
    if (type === 'LOUNGE') {
      return this.loungeDetail(item);
    }
    if (type === 'PARKING') {
      return this.parkingDetail(item);
    }
    if (type === 'FAST_TRACK') {
      return this.fastTrackDetail(item);
    }
    return null;
  }

  private serviceTypeLabel(type: string): string {
    if (!type) {
      return '';
    }
    return type
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  /** Outbound + (optional) return rail rows read from the basket detail. */
  private railRows(item: ChatUpcomingTripLeg): JourneyRow[] {
    const detail = item.detail as RailLegDetail | null;
    if (!detail) {
      return [];
    }
    const rows: JourneyRow[] = [];
    if (detail.outwardDetail) {
      rows.push(this.buildRailRow('Outbound', detail.outwardDetail));
    }
    if (detail.inwardDetail) {
      rows.push(this.buildRailRow('Return', detail.inwardDetail));
    }
    return rows;
  }

  /** First-seen-order de-duplication of non-empty strings. */
  private uniqueValues(values: Array<string | null | undefined>): string[] {
    return values.reduce<string[]>((unique, value) => {
      if (value && !unique.includes(value)) {
        unique.push(value);
      }
      return unique;
    }, []);
  }

  private buildRailRow(label: string, ward: RailWardDetail): JourneyRow {
    const legs = ward.supplierTickets?.[0]?.legs ?? [];
    const operator = this.uniqueValues(
      legs.map((leg) => leg.operator).filter((op) => !op?.includes('Alternative service'))
    ).join(' - ');
    return {
      label,
      date: this.formatLongDate(ward.departureDateTime),
      operator,
      departTime: this.formatTime(ward.departureDateTime),
      departPoint: ward.departureStationName ?? '',
      arriveTime: this.formatTime(ward.arrivalDateTime),
      arrivePoint: ward.arrivalStationName ?? ''
    };
  }

  /** Outbound + (optional) return flight / Eurostar rows read from the basket detail. */
  private flightRows(item: ChatUpcomingTripLeg): JourneyRow[] {
    const detail = item.detail as FlightLegDetail | null;
    if (!detail) {
      return [];
    }
    const rows: JourneyRow[] = [];
    const outbound = this.buildFlightRow('Outbound', detail.outboundFlights);
    if (outbound) {
      rows.push(outbound);
    }
    const inbound = this.buildFlightRow('Return', detail.inboundFlights);
    if (inbound) {
      rows.push(inbound);
    }
    return rows;
  }

  private buildFlightRow(label: string, segments?: FlightSegment[]): JourneyRow | null {
    if (!segments?.length) {
      return null;
    }
    const first = segments[0];
    const last = segments[segments.length - 1];
    const operator = this.uniqueValues(segments.map((seg) => seg.marketingCarrierName)).join(' - ');
    return {
      label,
      date: this.formatLongDate(first.dateTimeDeparting),
      operator,
      departTime: this.formatTime(first.dateTimeDeparting),
      departPoint: first.originAirportName ?? first.originAirport ?? '',
      arriveTime: this.formatTime(last.dateTimeArriving),
      arrivePoint: last.destinationAirportName ?? last.destinationAirport ?? ''
    };
  }

  /** Single row read from a taxi / cab leg's basket detail. Unlabelled - unlike rail/flight
   * there is only ever one row, so the item header's service tag already names it. */
  private taxiRows(item: ChatUpcomingTripLeg): JourneyRow[] {
    const detail = item.detail as TaxiLegDetail | null;
    if (!detail) {
      return [];
    }
    const operator = [detail.supplierName, detail.vehicleType].filter(Boolean).join(' · ');
    return [
      {
        label: '',
        date: this.formatLongDate(detail.departs),
        operator,
        departTime: this.formatTime(detail.departs),
        departPoint: this.taxiLocation(detail.fromLocation),
        arriveTime: '',
        arrivePoint: this.taxiLocation(detail.toLocation)
      }
    ];
  }

  /** Fallback for a 'journey'-kind type with no dedicated row builder — avoids silently mapping it through taxiRows. */
  private genericRow(item: ChatUpcomingTripLeg): JourneyRow[] {
    return [
      {
        label: this.serviceTypeLabel(item.serviceType),
        date: this.genericDates(item),
        operator: '',
        departTime: '',
        departPoint: '',
        arriveTime: '',
        arrivePoint: ''
      }
    ];
  }

  private carDetail(item: ChatUpcomingTripLeg): ServiceDetailView | null {
    const detail = item.detail as CarLegDetail | null;
    if (!detail) {
      return null;
    }
    return {
      title: [detail.vendorName, detail.car?.class].filter(Boolean).join(' · '),
      subtitle: this.carLocation(detail.pickUpLocation),
      rows: [
        { label: 'Pick-up', value: this.formatDateTime(detail.pickUpDatetime) },
        { label: 'Drop-off', value: this.formatDateTime(detail.dropOffDatetime) }
      ]
    };
  }

  private loungeDetail(item: ChatUpcomingTripLeg): ServiceDetailView | null {
    const detail = item.detail as LoungeLegDetail | null;
    if (!detail) {
      return null;
    }
    const subtitle = [
      detail.destination ?? detail.airportIata,
      detail.terminal ? `Terminal ${detail.terminal}` : ''
    ]
      .filter(Boolean)
      .join(' · ');
    return {
      title: detail.name ?? '',
      subtitle,
      rows: [{ label: 'Arrival', value: this.formatDateTime(detail.arrivalDatetime) }]
    };
  }

  private parkingDetail(item: ChatUpcomingTripLeg): ServiceDetailView | null {
    const detail = item.detail as ParkingLegDetail | null;
    if (!detail) {
      return null;
    }
    return {
      title: detail.name ?? '',
      subtitle: detail.airportIata ?? '',
      rows: [
        { label: 'From', value: this.formatDateTime(detail.arrivalDatetime) },
        { label: 'To', value: this.formatDateTime(detail.departDatetime) }
      ]
    };
  }

  private fastTrackDetail(item: ChatUpcomingTripLeg): ServiceDetailView | null {
    const detail = item.detail as FastTrackLegDetail | null;
    if (!detail) {
      return null;
    }
    const subtitle = [detail.airportIata, detail.terminal ? `Terminal ${detail.terminal}` : '']
      .filter(Boolean)
      .join(' · ');
    return {
      title: detail.name ?? '',
      subtitle,
      rows: [{ label: 'Arrival', value: this.formatDateTime(detail.arrivalDatetime) }]
    };
  }

  private taxiLocation(location?: TaxiLocation): string {
    if (!location) {
      return '';
    }
    const street = this.uniqueValues([location.buildingName, location.streetAndNumber]).join(' ');
    return [street, location.town].filter(Boolean).join(', ');
  }

  private carLocation(location?: CarLocation): string {
    if (!location) {
      return '';
    }
    return [location.address ?? location.name, location.iata].filter(Boolean).join(' · ');
  }

  private hotelName(item: ChatUpcomingTripLeg): string {
    return (item.detail as HotelLegDetail | null)?.hotelName ?? '';
  }

  private hotelLocation(item: ChatUpcomingTripLeg): string {
    const address = (item.detail as HotelLegDetail | null)?.hotelAddress;
    return address ? this.myBookingsService.getHotelLocation(address) : '';
  }

  /** Check-in/check-out/nights derived once from the room detail (falling back to the leg dates). */
  private hotelStay(item: ChatUpcomingTripLeg): {
    checkIn: string;
    checkOut: string;
    nights: number | null;
  } {
    const room = this.hotelDetail(item)?.rooms?.[0];
    const checkIn = room?.checkIn ?? item.outboundDate;
    const checkOut = room?.checkOut ?? item.inboundDate;
    return {
      checkIn: this.formatLongDate(checkIn),
      checkOut: this.formatLongDate(checkOut),
      nights: this.nightsBetween(checkIn, checkOut)
    };
  }

  private nightsBetween(
    checkIn: string | null | undefined,
    checkOut: string | null | undefined
  ): number | null {
    if (!checkIn || !checkOut) {
      return null;
    }
    const start = new Date(checkIn);
    const end = new Date(checkOut);
    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      return null;
    }
    const msPerDay = 1000 * 60 * 60 * 24;
    const diff = Math.round((this.startOfDay(end) - this.startOfDay(start)) / msPerDay);
    return diff > 0 ? diff : null;
  }

  /** Generic leg dates for non-rail/non-hotel services (flight/car/etc.). */
  private genericDates(item: ChatUpcomingTripLeg): string {
    const out = this.formatLongDate(item.outboundDate);
    const back = this.formatLongDate(item.inboundDate);
    if (out && back) {
      return `${out} - ${back}`;
    }
    return out || back;
  }

  private hotelDetail(item: ChatUpcomingTripLeg): HotelLegDetail | null {
    return item.detail as HotelLegDetail | null;
  }

  private startOfDay(date: Date): number {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  }

  /** "Thurs 4th Jun 07:30" — long date + 24h time, for single-point services. */
  private formatDateTime(dateStr: string | null | undefined): string {
    return [this.formatLongDate(dateStr), this.formatTime(dateStr)].filter(Boolean).join(' ');
  }

  /** "Thurs 4th Jun" — the single date style used across every item kind on the card. */
  private formatLongDate(dateStr: string | null | undefined): string {
    if (!dateStr) {
      return '';
    }
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) {
      return '';
    }
    const day = date.getDate();
    const weekday = date.toLocaleDateString('en-GB', { weekday: 'short' });
    const month = date.toLocaleDateString('en-GB', { month: 'short' });
    return `${weekday} ${day}${this.getOrdinal(day)} ${month}`;
  }

  /** "07:03" 24h time. */
  private formatTime(dateStr: string | null | undefined): string {
    if (!dateStr) {
      return '';
    }
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) {
      return '';
    }
    return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
  }

  private getOrdinal(n: number): string {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return s[(v - 20) % 10] || s[v] || s[0];
  }
}
